package com.karyadina.cloudsync.service

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlinx.browser.document

/**
 * Web Google Auth Service using Google Identity Services JS SDK.
 * Reads the OAuth Client ID from a <meta name="google-signin-client_id"> tag in index.html.
 */
class WebGoogleAuthService : GoogleAuthService {
    
    private val _authState = MutableStateFlow<AuthState>(AuthState.Unauthenticated)
    override val authState: StateFlow<AuthState> = _authState.asStateFlow()
    
    private var cachedToken: String? = null

    private fun getWebClientId(): String {
        val meta = document.querySelector("meta[name='google-signin-client_id']")
        return meta?.getAttribute("content") ?: ""
    }

    override suspend fun authenticate(activity: Any?): Result<String> {
        return suspendCancellableCoroutine { continuation ->
            try {
                val googleObj = js("window.google")
                if (googleObj == null || googleObj === undefined) {
                    continuation.resume(Result.failure(Exception("Google Identity Services SDK not loaded")))
                    return@suspendCancellableCoroutine
                }

                val options = js("{}")
                options.client_id = getWebClientId()
                options.scope = "https://www.googleapis.com/auth/drive.appdata"
                options.callback = { response: dynamic ->
                    val error = response.error
                    val token = response.access_token
                    if (error != undefined && error != null) {
                        continuation.resume(Result.failure(Exception("Auth failed: $error")))
                    } else if (token != undefined && token != null) {
                        _authState.value = AuthState.Authenticated(userId = "web-user", email = "Web User")
                        cachedToken = token as String
                        continuation.resume(Result.success(token))
                    } else {
                        continuation.resume(Result.failure(Exception("Unknown auth error")))
                    }
                }

                val client = googleObj.accounts.oauth2.initTokenClient(options)
                client.requestAccessToken()
            } catch (e: Exception) {
                continuation.resume(Result.failure(e))
            }
        }
    }
    
    override suspend fun getToken(): Result<String> {
        val token = cachedToken
        return if (token != null) {
            Result.success(token)
        } else {
            authenticate(null)
        }
    }
    
    override suspend fun logout(): Result<Unit> {
        _authState.value = AuthState.Unauthenticated
        cachedToken = null
        return Result.success(Unit)
    }
    
    override fun isAuthenticated(): Boolean {
        return _authState.value is AuthState.Authenticated
    }
    
    override fun getUserId(): String? {
        return when (val state = _authState.value) {
            is AuthState.Authenticated -> state.userId
            else -> null
        }
    }
}
